import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { ArrowLeft, ArrowRight, Loader2, ShieldCheck } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function formatApiErrorDetail(detail) {
    if (detail == null) return "Something went wrong. Please try again.";
    if (typeof detail === "string") return detail;
    if (Array.isArray(detail))
        return detail
            .map((e) =>
                e && typeof e.msg === "string" ? e.msg : JSON.stringify(e),
            )
            .filter(Boolean)
            .join(" ");
    if (detail && typeof detail.msg === "string") return detail.msg;
    return String(detail);
}

export default function Setup() {
    const navigate = useNavigate();
    const [setupDone, setSetupDone] = useState(null);
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [confirm, setConfirm] = useState("");
    const [error, setError] = useState("");
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        axios
            .get(`${API}/auth/setup-status`)
            .then(({ data }) => setSetupDone(data.setup_done))
            .catch(() => setSetupDone(false));
    }, []);

    const submit = async (e) => {
        e.preventDefault();
        setError("");
        if (password.length < 8) {
            setError("Password must be at least 8 characters.");
            return;
        }
        if (password !== confirm) {
            setError("Passwords do not match.");
            return;
        }
        setBusy(true);
        try {
            await axios.post(
                `${API}/auth/setup`,
                { email, password },
                { withCredentials: true },
            );
            toast.success("Admin account created. Welcome to your studio.");
            navigate("/admin");
        } catch (err) {
            setError(
                formatApiErrorDetail(err.response?.data?.detail) ||
                    err.message,
            );
        } finally {
            setBusy(false);
        }
    };

    return (
        <div
            className="min-h-screen bg-ink text-paper flex flex-col lg:flex-row"
            data-testid="setup-page"
        >
            <div className="lg:w-1/2 flex flex-col justify-between p-8 lg:p-14 min-h-[180px]">
                <Link
                    to="/"
                    data-testid="setup-back-home"
                    className="group mono text-[11px] tracking-[0.25em] text-paper2/60 inline-flex items-center gap-2 hover:text-paper transition-colors"
                >
                    <ArrowLeft className="h-3.5 w-3.5 transition-transform group-hover:-translate-x-1" />
                    BACK TO SITE
                </Link>
                <div className="hidden lg:block">
                    <p className="mono text-xs tracking-[0.3em] text-sage">
                        _beingdevang / FIRST RUN
                    </p>
                    <p className="serif-it text-3xl xl:text-4xl mt-6 max-w-sm leading-snug text-paper2">
                        One key. One owner. This door only opens once.
                    </p>
                </div>
            </div>

            <div className="lg:w-1/2 flex items-center justify-center p-8 lg:p-14">
                {setupDone === null ? (
                    <p className="mono text-xs tracking-[0.25em] text-muted2">
                        CHECKING…
                    </p>
                ) : setupDone ? (
                    <div
                        className="w-full max-w-md border border-linedark bg-ink2 p-8 lg:p-12"
                        data-testid="setup-done-state"
                    >
                        <ShieldCheck className="h-8 w-8 text-sage" />
                        <h1 className="font-display text-3xl tracking-tight mt-4">
                            SETUP COMPLETE
                        </h1>
                        <p className="text-sm text-muted2 mt-3 leading-relaxed">
                            An admin account already exists. This page is
                            permanently closed — use the login page instead.
                        </p>
                        <Link
                            to="/login"
                            data-testid="setup-login-link"
                            className="group mt-8 inline-flex items-center gap-3 bg-sage text-ink px-6 py-3 mono text-[11px] tracking-[0.25em] uppercase transition-colors hover:bg-paper"
                        >
                            GO TO LOGIN
                            <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                        </Link>
                    </div>
                ) : (
                    <form
                        onSubmit={submit}
                        className="w-full max-w-md border border-linedark bg-ink2 p-8 lg:p-12"
                        data-testid="setup-form"
                    >
                        <p className="mono text-[10px] tracking-[0.3em] text-muted2">
                            01 — CLAIM THE KEYS
                        </p>
                        <h1 className="font-display text-3xl md:text-4xl tracking-tight mt-3">
                            CREATE ADMIN
                        </h1>

                        <label className="block mt-10">
                            <span className="mono text-[10px] tracking-[0.25em] text-muted2">
                                EMAIL
                            </span>
                            <input
                                type="email"
                                required
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                data-testid="setup-email-input"
                                className="mt-2 w-full bg-transparent border-b border-linedark focus:border-sage outline-none py-3 text-base transition-colors"
                            />
                        </label>

                        <label className="block mt-8">
                            <span className="mono text-[10px] tracking-[0.25em] text-muted2">
                                PASSWORD (MIN. 8 CHARACTERS)
                            </span>
                            <input
                                type="password"
                                required
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                data-testid="setup-password-input"
                                className="mt-2 w-full bg-transparent border-b border-linedark focus:border-sage outline-none py-3 text-base transition-colors"
                            />
                        </label>

                        <label className="block mt-8">
                            <span className="mono text-[10px] tracking-[0.25em] text-muted2">
                                CONFIRM PASSWORD
                            </span>
                            <input
                                type="password"
                                required
                                value={confirm}
                                onChange={(e) => setConfirm(e.target.value)}
                                data-testid="setup-confirm-input"
                                className="mt-2 w-full bg-transparent border-b border-linedark focus:border-sage outline-none py-3 text-base transition-colors"
                            />
                        </label>

                        {error && (
                            <p
                                className="mt-6 text-sm text-red-300 border border-red-400/30 bg-red-400/10 px-4 py-3"
                                data-testid="setup-error"
                            >
                                {error}
                            </p>
                        )}

                        <button
                            type="submit"
                            disabled={busy}
                            data-testid="setup-submit-button"
                            className="group mt-10 w-full inline-flex items-center justify-center gap-3 bg-sage text-ink px-8 py-4 mono text-[11px] tracking-[0.25em] uppercase transition-colors hover:bg-paper disabled:opacity-60"
                        >
                            {busy ? (
                                <Loader2 className="h-4 w-4 animate-spin" />
                            ) : (
                                <>
                                    CREATE MY ACCOUNT
                                    <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1.5" />
                                </>
                            )}
                        </button>
                    </form>
                )}
            </div>
        </div>
    );
}
